import { ShieldCheck } from "lucide-react";
import type { CheckMode, Team } from "@/lib/api/types";
import { useSetCheckMode, useTeamMembers } from "@/hooks/queries/use-teams";
import { useOnboardingState } from "@/hooks/queries/use-onboarding";
import { useAuthStore } from "@/stores/auth-store";

function errorText(error: unknown): string | null {
  if (!error) return null;
  return error instanceof Error ? error.message : "Something went wrong. Try again.";
}

/** The three cadences, most thorough first (ENG-395). */
export const CHECK_MODE_OPTIONS: { value: CheckMode; label: string; hint: string }[] = [
  {
    value: "every",
    label: "Every",
    hint: "Review, QA and security checks after every feature. Most thorough, slowest.",
  },
  {
    value: "smart",
    label: "Smart (default)",
    hint: "Checks after each phase, plus right away for risky features.",
  },
  {
    value: "end",
    label: "End",
    hint: "One full round of checks once all features are built. Fastest.",
  },
];

const VALID = new Set<string>(CHECK_MODE_OPTIONS.map((o) => o.value));

/** A stored or passed value, narrowed to a known mode; anything else is "smart". */
export function toCheckMode(raw: unknown): CheckMode {
  return typeof raw === "string" && VALID.has(raw) ? (raw as CheckMode) : "smart";
}

/**
 * Project setting: how often builds run their review / QA / security checks
 * (ENG-395). Each run can still pick a different cadence when it is sent.
 * Project admins, organization admins and platform admins can change it;
 * everyone else sees it read-only.
 */
export function CheckModeCard({ team }: { team: Team }) {
  const me = useAuthStore((s) => s.user);
  const { data: onboarding } = useOnboardingState();
  const { data: members } = useTeamMembers(team.id);
  const setCheckMode = useSetCheckMode(team.id);

  const current = toCheckMode(setCheckMode.data?.data.checkMode ?? team.checkMode);
  const selected = setCheckMode.isPending ? setCheckMode.variables : current;
  const myRole = members?.find((m) => m.user.id === me?.id)?.role;
  const canEdit =
    !!me?.isGlobalAdmin ||
    (onboarding?.step === "ready" && onboarding.organizationRole === "admin") ||
    myRole === "admin";
  const disabled = !canEdit || setCheckMode.isPending;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="flex items-center gap-2 text-lg font-medium">
          <ShieldCheck className="h-4 w-4" />
          Build checks
        </h3>
        <p className="text-sm text-muted-foreground">
          How often builds from this project stop to check their work. Each run can still change it.
        </p>
      </div>

      <div role="radiogroup" aria-label="Build checks" className="space-y-2">
        {CHECK_MODE_OPTIONS.map((option) => (
          <label
            key={option.value}
            className="flex items-start gap-3 rounded-lg border border-white/[0.08] p-3 text-sm"
          >
            <input
              type="radio"
              name={`check-mode-${team.id}`}
              value={option.value}
              className="mt-0.5 h-4 w-4"
              checked={selected === option.value}
              disabled={disabled}
              onChange={() => {
                if (option.value !== current) setCheckMode.mutate(option.value);
              }}
            />
            <span>
              <span className="block font-medium">{option.label}</span>
              <span className="block text-xs text-muted-foreground">{option.hint}</span>
            </span>
          </label>
        ))}
      </div>

      {!canEdit ? (
        <p className="text-xs text-muted-foreground">Only a project admin can change this.</p>
      ) : null}
      {errorText(setCheckMode.error) ? (
        <p className="text-xs text-destructive">{errorText(setCheckMode.error)}</p>
      ) : null}
    </div>
  );
}
